import React, { useState } from "react";
import {
    PlaydateInvite,
} from "../../../gql/types";
import PlaydateInviteInPlanningView from "../view/PlaydateInviteOrganizerInPlanningView";
import PlaydateInviteFinalizeDecisionView from "../view/PlaydateInviteFinalizeDecisionView";
import { DayPartSelection } from "../../time/types";
import PlaydateInviteEditWorkflow from "./PlaydateInviteOrganizerEditWorkflow";
import { useSubmitFinalizeInvite } from "service/playdate/requests/useSubmitFinalizeInvite";
import { useToastContext } from "contexts/ToastContext";

interface Props {
    invite: PlaydateInvite;
    onBack: () => void;
    onCancelInvite: () => void;

    /** Called after finalization completes */
    onFinalize: (updatedInvite?: PlaydateInvite) => void;
    onUpdate: (updatedInvite?: PlaydateInvite) => void;

    /** Optional override of the finalization mutation */
    onFinalizeSelectionOverride?: (selection: DayPartSelection) => Promise<PlaydateInvite>;

    baseZIndex?: number;
}

type Stage = "viewPlanning" | "finalize" | "modify";

const PlaydateInviteOrganizerPlanningWorkflow: React.FC<Props> = ({
    invite,
    onBack,
    onFinalize,
    onUpdate,
    onCancelInvite,
    onFinalizeSelectionOverride,
    baseZIndex,
}) => {
    const [stage, setStage] = useState<Stage>("viewPlanning");
    const { submitFinalize, loading } = useSubmitFinalizeInvite();
    const { triggerToastEvent } = useToastContext();

    const handleSubmitFinalize = async (selection: DayPartSelection | null) => {
        await submitFinalize({
            invite,
            selection,
            onFinalizeSelectionOverride,
            onCompletion: (success, updatedInvite) => {
                if (!success) {
                    triggerToastEvent();
                }

                if (success) {
                    onFinalize(updatedInvite);
                }
            },
        });
    };

    if (stage === "finalize") {
        return (
            <PlaydateInviteFinalizeDecisionView
                invite={invite}
                onBack={() => setStage("viewPlanning")}
                onConfirm={handleSubmitFinalize}
                loading={loading}
            />
        );
    }

    if (stage === "modify") {
        return (
            <PlaydateInviteEditWorkflow
                invite={invite}
                onBack={() => setStage("viewPlanning")}
                onDataChange={(_success, update?: PlaydateInvite) => {
                    onUpdate(update);
                }}
                baseZIndex={baseZIndex}
            />
        );
    }

    return (
        <PlaydateInviteInPlanningView
            invite={invite}
            onBack={onBack}
            onFinalize={() => setStage("finalize")}
            onModify={() => setStage("modify")}
            onDelete={onCancelInvite}
            baseZIndex={baseZIndex}
        />
    );
};

export default PlaydateInviteOrganizerPlanningWorkflow;
